/**
 * Monthly Rental Highlight Section — Home Page
 *
 * Dark teal background section promoting the monthly rental plans.
 * Leads with one clear entry point (plan structure without prices, since
 * pricing varies per item — customers select gear to see their monthly rate).
 */

import { Link } from 'react-router';
import { ArrowRight, Check } from 'lucide-react';
import { usePageSection } from '@/hooks/usePageContent';

export interface MonthlyRentalHighlightContent {
    heading: string;
    subheading: string;
    ctaLabel: string;
    ctaUrl: string;
    planBadges: { label: string; description: string }[];
}

const DEFAULT_MONTHLY_RENTAL_HIGHLIGHT: MonthlyRentalHighlightContent = {
    heading: 'Always Have the Right Gear',
    subheading:
        'Growing babies need different gear every few months. Rent by the month, swap when they grow, and return when you\'re done — no big purchase, no clutter.',
    ctaLabel: 'Browse monthly rentals',
    ctaUrl: '/catalogue?mode=monthly-rental',
    planBadges: [],
};

const MONTHLY_RENTAL_BENEFITS = [
    'Gear that grows with your baby — swap when they do',
    'Deep-cleaned and safety-checked before every rental',
    'No contract — return it whenever you\'re ready',
];

export const MonthlyRentalHighlightSection = () => {
    const cms = usePageSection<MonthlyRentalHighlightContent>('home', 'monthly-rental-highlight');
    const content = cms ?? DEFAULT_MONTHLY_RENTAL_HIGHLIGHT;

    return (
        <section
            aria-labelledby="monthly-rental-highlight-heading"
            className="relative isolate overflow-hidden bg-[linear-gradient(135deg,rgba(10,83,81,1)_0%,rgba(15,118,110,1)_52%,rgba(18,96,95,1)_100%)] py-20 sm:py-24"
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] lg:gap-16">

                    {/* Left: copy */}
                    <div className="delight-rise">
                        <h2
                            id="monthly-rental-highlight-heading"
                            className="max-w-xl font-display text-4xl leading-tight text-white sm:text-5xl lg:text-6xl"
                        >
                            {content.heading}
                        </h2>
                        <p className="mt-5 max-w-lg text-lg leading-8 text-white/90">
                            {content.subheading}
                        </p>

                        <ul className="mt-7 space-y-3" aria-label="Monthly rental benefits">
                            {MONTHLY_RENTAL_BENEFITS.map((benefit, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent-on-dark" aria-hidden="true" />
                                    <span className="text-sm leading-6 text-white/80">{benefit}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Right: pricing note + CTA */}
                    <div className="delight-rise delight-delay-1 rounded-2xl border border-white/20 bg-white/10 p-8 backdrop-blur-sm">
                        <p className="text-base leading-7 text-white/80">
                            Every item has its own monthly rental rate. The longer you rent, the lower your monthly rate — your exact cost shows when you choose an item.
                        </p>

                        <Link
                            to={content.ctaUrl}
                            className="group mt-7 inline-flex min-h-[48px] items-center gap-2 rounded-[1rem] bg-accent px-6 text-sm font-bold text-accent-foreground transition-[transform,box-shadow,background-color] duration-200 hover:bg-accent/90 motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-[0_14px_28px_rgba(19,30,29,0.22)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
                        >
                            {content.ctaLabel}
                            <ArrowRight className="h-4 w-4 flex-shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
                        </Link>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default MonthlyRentalHighlightSection;
